import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'

interface ProxySource {
  id: number
  name: string
  source_url: string
  selector_rules: Record<string, unknown> | null
  is_active: boolean
  created_at: string | null
}

export default function SourcesPage() {
  const { api } = useApi()
  const [sources, setSources] = useState<ProxySource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formName, setFormName] = useState('')
  const [formUrl, setFormUrl] = useState('')
  const [_editingId, _setEditingId] = useState<number | null>(null)
  
  const fetchSources = async () => {
    try {
      const res = await api.get('/proxies/sources')
      setSources(res.data)
      setError(null)
    } catch (err: unknown) {
      if (err instanceof Error) setError(err.message)
    } finally {
      setLoading(false)
    }
  }
  
  useEffect(() => { fetchSources() }, [])
  
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await api.post('/proxies/sources', {
        name: formName,
        source_url: formUrl,
        selector_rules: {},
      })
      setFormName('')
      setFormUrl('')
      setShowForm(false)
      fetchSources()
    } catch {
      setError('Failed to create source')
    }
  }
  
  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await api.put(`/proxies/sources/${id}`, { is_active: isActive ? 1 : 0 })
      fetchSources()
    } catch {
      setError('Failed to update source')
    }
  }
  
  const handleDelete = async (id: number) => {
    if (!confirm('Delete this source?')) return
    try {
      await api.delete(`/proxies/sources/${id}`)
      fetchSources()
    } catch {
      setError('Failed to delete source')
    }
  }
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }
  
  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Proxy Sources</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          {showForm ? 'Cancel' : '+ Add Source'}
        </button>
      </div>
      
      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      
      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Source</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                required
                placeholder="e.g. FreeProxyList"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="url"
                value={formUrl}
                onChange={e => setFormUrl(e.target.value)}
                required
                placeholder="https://example.com/proxies"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Create Source
          </button>
        </form>
      )}
      
      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sources.map(source => (
              <tr key={source.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{source.name}</td>
                <td className="px-6 py-4 text-sm text-gray-600 truncate max-w-xs">{source.source_url}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleToggleActive(source.id, !source.is_active)}
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full cursor-pointer ${
                      source.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                  >
                    {source.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {source.created_at ? new Date(source.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleDelete(source.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {sources.length === 0 && (
          <div className="p-8 text-center text-gray-500">No proxy sources found</div>
        )}
      </div>
    </div>
  )
}
